import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import i18n from "../lib/i18n";

const AuthContext = createContext(null);

// Las fotos y documentos privados se guardan en una caché aparte del service
// worker (casarent-img-*). Se borra al cerrar la sesión, también cuando la
// cierra el servidor, para que no queden en un dispositivo compartido.
async function clearPrivateCaches() {
  try {
    if (typeof caches === "undefined") return;
    const keys = await caches.keys();
    await Promise.all(keys.filter((k) => k.startsWith("casarent-img-")).map((k) => caches.delete(k)));
  } catch {}
}

export function AuthProvider({ children }) {
  // null = checking, false = anonymous, object = logged in
  const [user, setUser] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      if (data?.language && data.language !== i18n.language) {
        i18n.changeLanguage(data.language);
        localStorage.setItem("casarent_lang", data.language);
      }
      return data;
    } catch (e) {
      if (e?.response?.status === 401) await clearPrivateCaches();
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data?.token) localStorage.setItem("casarent_token", data.token);
    setUser(data.user);
    if (data.user?.language) {
      i18n.changeLanguage(data.user.language);
      localStorage.setItem("casarent_lang", data.user.language);
    }
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    localStorage.removeItem("casarent_token");
    await clearPrivateCaches();
    setUser(false);
  };

  const updateProfile = async (patch) => {
    const { data } = await api.patch("/auth/me", patch);
    setUser(data);
    if (patch.language) {
      i18n.changeLanguage(patch.language);
      localStorage.setItem("casarent_lang", patch.language);
    }
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, setUser, refresh, refreshUser: refresh, login, logout, updateProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
